import { createSelector } from "@reduxjs/toolkit";

import { RootState } from "app/store";

import { DiagnosticsConfiguration } from "types/AppConfig";
import {
    selectDiagnosticsConfiguration,
    selectDiagnosticsMaxDurationInMsPairs,
} from "features/config/store/selectors";
import { DiagnosticsLogNode } from "../types/DiagnosticsLogNode";
import { mapDiagnosticsLogNodeToTreeNodeRecursively } from "./helpers";
import { FlowStep } from "../types/FlowStep";

const selectDiagnosticsLogs = (state: RootState) =>
    state.diagnostics.diagnosticsData.diagnosticsLogNodes;
const selectDiagnosticsLivestreamStartupFlowSteps = (state: RootState) =>
    state.diagnostics.diagnosticsData.livestreamStartupFlowSteps;

const selectDiagnosticsLogsAsTreeNodes = createSelector(
    [selectDiagnosticsLogs],
    (diagnosticsLogNodes: DiagnosticsLogNode[] | undefined) =>
        diagnosticsLogNodes?.map((logNode) =>
            mapDiagnosticsLogNodeToTreeNodeRecursively(logNode),
        ),
);

const selectDiagnosticsLogsLiveStreamStartUpFlow = createSelector(
    [
        selectDiagnosticsLivestreamStartupFlowSteps,
        selectDiagnosticsMaxDurationInMsPairs,
    ],
    (
        diagnosticsLogNodes: FlowStep[] | undefined,
        maxDurationInMsPairs: Map<number, number> | undefined,
    ) =>
        diagnosticsLogNodes?.map((step) => ({
            ...step,
            maxAllowedDuration: maxDurationInMsPairs?.get(step.id),
        })) ?? [],
);

const selectDiagnosticsLogsRefreshIntervalInSeconds = createSelector(
    [selectDiagnosticsConfiguration],
    (diagnosticsConfiguration?: DiagnosticsConfiguration) =>
        diagnosticsConfiguration?.diagnosticsLogsRefreshIntervalInSeconds ?? 0,
);

const selectDiagnosticsSettingsQueryLookbackDuration = createSelector(
    [selectDiagnosticsConfiguration],
    (diagnosticsConfiguration?: DiagnosticsConfiguration) =>
        diagnosticsConfiguration?.diagnosticsQueryLookbackDuration,
);
const selectServerTimeCallDurationInMilliseconds = (state: RootState) =>
    state.diagnostics.duration ?? 0;

export const diagnosticsOperationSelectors = {
    selectDiagnosticsLogs,
    selectDiagnosticsLogsAsTreeNodes,
    selectDiagnosticsLogsRefreshIntervalInSeconds,
    selectDiagnosticsSettingsQueryLookbackDuration,
    selectServerTimeCallDurationInMilliseconds,
    selectDiagnosticsLogsLiveStreamStartUpFlow,
};
